import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Globe, Users, CheckCircle2, ArrowRight, Clock,
  Shield, Target, Heart, Compass, Phone, Mail,
  Truck, Plane, Ship, Warehouse, FileCheck, Package,
  Star, Sparkles, HandHeart, MapPin, Building2, Handshake
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

// Asset URLs - Local images from public folder
const BIANCA_PHOTO = "/assets/founder-bianca-silva.jpg";
const ADDY_PHOTO = "/assets/founder-addy-parra.jpg";

const AboutPage = () => {
  const { t } = useTranslation();

  // Core values - All 7 Christian values (LOCKED CONTENT - DO NOT MODIFY)
  const values = [
    { icon: Compass, title: t('values.faith.title'), description: t('values.faith.description') },
    { icon: Shield, title: t('values.integrity.title'), description: t('values.integrity.description') },
    { icon: Heart, title: t('values.service.title'), description: t('values.service.description') },
    { icon: Star, title: t('values.excellence.title'), description: t('values.excellence.description') },
    { icon: Target, title: t('values.reliability.title'), description: t('values.reliability.description') },
    { icon: HandHeart, title: t('aboutPage.values.responsibility.title'), description: t('aboutPage.values.responsibility.description') },
    { icon: Handshake, title: t('aboutPage.values.ownership.title'), description: t('aboutPage.values.ownership.description') },
  ];

  // Services coordinated - icons with translated text
  const servicesCoordinated = [
    { icon: Truck, text: t('aboutPage.services.roadFreight') },
    { icon: Ship, text: t('aboutPage.services.oceanFreight') },
    { icon: Plane, text: t('aboutPage.services.airFreight') },
    { icon: Warehouse, text: t('aboutPage.services.warehousing') },
    { icon: FileCheck, text: t('aboutPage.services.customs') },
    { icon: Package, text: t('aboutPage.services.specialCargo') }
  ];

  // Company facts - labels translated, values kept as-is (legal identifiers)
  const companyFacts = [
    { label: t('aboutPage.facts.companyName'), value: 'Elementary Cargo' },
    { label: t('aboutPage.facts.location'), value: 'Nederland' },
    { label: t('aboutPage.facts.kvk'), value: '42041124' },
    { label: t('aboutPage.facts.eori'), value: 'NL3785680245' },
    { label: t('aboutPage.facts.vat'), value: 'NL005449408B56' },
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="about-page">
      <SEO 
        titleKey="seo.about.title"
        descriptionKey="seo.about.description"
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 Q 25 25, 50 50 T 100 50' stroke='%23C7A24E' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 100px'
          }} />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-gold font-space text-sm tracking-widest uppercase pb-2 border-b-2 border-gold/60 mb-6">
              {t('aboutPage.badge')}
            </span>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Moving with Purpose
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed">
              {t('aboutPage.heroText')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('aboutPage.whoWeAreBadge')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                {t('aboutPage.whoWeAreTitle')}
              </h2>
              <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
                <p>{t('aboutPage.whoWeAreP1')}</p>
                <p>{t('aboutPage.whoWeAreP2')}</p>

                <div className="bg-navy-900/5 p-6 rounded-xl border-l-4 border-gold">
                  <p className="font-semibold text-navy-900 mb-2">{t('aboutPage.clarificationTitle')}</p>
                  <p className="text-base">{t('aboutPage.clarificationText')}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80"
                  alt="Container port"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gold text-navy-900 px-6 py-4 rounded-xl shadow-lg">
                <div className="text-sm font-medium">{t('aboutPage.basedIn')}</div>
                <div className="text-xl font-bold">{t('aboutPage.netherlands')}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-20 md:py-28 bg-navy-800">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('aboutPage.leadershipBadge')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('aboutPage.founderLedTitle')}
            </h2>
            <p className="text-lg text-platinum-light max-w-2xl mx-auto">
              {t('aboutPage.founderLedSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Addy Parra */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="relative inline-block mb-6">
                <div className="w-64 h-80 rounded-2xl overflow-hidden shadow-2xl mx-auto">
                  <img src={ADDY_PHOTO} alt="Addy Parra - Co-Founder" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-gold text-navy-900 px-6 py-2 rounded-full shadow-lg whitespace-nowrap">
                  <span className="font-semibold">{t('aboutPage.coFounder')}</span>
                </div>
              </div>
              <h3 className="font-space text-2xl font-bold text-white mb-2">Addy Parra</h3>
              <p className="text-gold text-sm font-medium mb-3">{t('aboutPage.addyRole')}</p>
              <p className="text-platinum-light leading-relaxed max-w-sm mx-auto">{t('aboutPage.addyBio')}</p>
            </motion.div>

            {/* Bianca Silva */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="relative inline-block mb-6">
                <div className="w-64 h-80 rounded-2xl overflow-hidden shadow-2xl mx-auto">
                  <img src={BIANCA_PHOTO} alt="Bianca Silva - Co-Founder" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-gold text-navy-900 px-6 py-2 rounded-full shadow-lg whitespace-nowrap">
                  <span className="font-semibold">{t('aboutPage.coFounder')}</span>
                </div>
              </div>
              <h3 className="font-space text-2xl font-bold text-white mb-2">Bianca Silva</h3>
              <p className="text-gold text-sm font-medium mb-3">{t('aboutPage.biancaRole')}</p>
              <p className="text-platinum-light leading-relaxed max-w-sm mx-auto">{t('aboutPage.biancaBio')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Moving with Purpose - Meaning */}
      <section className="py-20 md:py-28 bg-offwhite">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('aboutPage.philosophyBadge')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-navy-900 mb-6">
                {t('aboutPage.philosophyTitle')}
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-platinum-light"
            >
              <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
                <p>{t('aboutPage.philosophyP1')}</p>
                <p>{t('aboutPage.philosophyP2')}</p>
                <p>{t('aboutPage.philosophyP3')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 md:py-28 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('aboutPage.coreValues')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('aboutPage.valuesTitle')}
            </h2>
            <p className="text-lg text-platinum-light max-w-2xl mx-auto">
              {t('aboutPage.valuesSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={`value-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-navy-800/50 border border-royal/20 hover:border-gold/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-7 h-7 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-white mb-2">{value.title}</h3>
                    <p className="text-platinum-light text-sm leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Coordinate */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('aboutPage.ourServicesBadge')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                {t('aboutPage.whatWeCoordinate')}
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                {t('aboutPage.whatWeCoordinateSubtitle')}
              </p>

              <div className="space-y-4">
                {servicesCoordinated.map((service) => (
                  <div key={service.text} className="flex items-center gap-4 p-4 bg-navy-900/5 rounded-lg">
                    <service.icon className="w-6 h-6 text-gold flex-shrink-0" />
                    <span className="text-slate-700">{service.text}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/services">
                  <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 hover:shadow-xl hover:shadow-gold/25 px-8 py-4">
                    {t('aboutPage.viewAllServices')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-navy-900 border-none">
                <CardContent className="p-8">
                  <h3 className="font-space text-xl font-bold text-white mb-6">{t('aboutPage.companyInfo')}</h3>
                  <div className="space-y-4">
                    {companyFacts.map((fact) => (
                      <div key={fact.label} className="flex justify-between items-center py-3 border-b border-royal/20 last:border-0">
                        <span className="text-platinum-light">{fact.label}</span>
                        <span className="font-semibold text-white">{fact.value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-royal/20">
                    <h4 className="font-semibold text-gold mb-4">{t('aboutPage.contact')}</h4>
                    <div className="space-y-3">
                      <a href="mailto:info@elementarycargo.com" className="flex items-center gap-3 text-platinum-light hover:text-gold transition-colors">
                        <Mail className="w-5 h-5" />
                        info@elementarycargo.com
                      </a>
                      <a href="tel:+31625381824" className="flex items-center gap-3 text-platinum-light hover:text-gold transition-colors">
                        <Phone className="w-5 h-5" />
                        +31 62 538 1824
                      </a>
                      <div className="flex items-center gap-3 text-platinum-light">
                        <MapPin className="w-5 h-5" />
                        {t('aboutPage.netherlands')}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Role Statement */}
      <section className="py-16 bg-navy-800">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="bg-navy-900/50 rounded-2xl p-8 md:p-12 border border-royal/20">
              <h3 className="font-space text-2xl md:text-3xl font-bold text-white mb-6">
                {t('aboutPage.roleStatementTitle')}
              </h3>
              <p className="text-lg text-platinum-light leading-relaxed">
                {t('aboutPage.roleStatementText')}
              </p>
              <div className="mt-8">
                <Link to="/how-it-works">
                  <Button variant="outline" className="border-gold/50 text-gold hover:bg-gold/10 px-8 py-4">
                    {t('aboutPage.howItWorks')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {t('aboutPage.ctaTitle')}
            </h2>
            <p className="text-xl text-platinum-light max-w-2xl mx-auto mb-10">
              {t('aboutPage.ctaDescription')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 hover:shadow-xl hover:shadow-gold/25 px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300">
                  {t('cta.getQuote')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-10 py-6 text-lg font-semibold rounded-lg">
                  {t('cta.contactUs')}
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 mt-8 text-platinum-light">
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-5 h-5" />
                <span>+31 62 538 1824</span>
              </a>
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-5 h-5" />
                <span>info@elementarycargo.com</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
